import { Loader2, MoreVertical, Pencil, Share2, Sparkles, Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { cn } from "~/lib/utils";

interface ConversationActionsMenuProps {
  // Absent for a row in the "Featured Sessions" rail, which has no
  // conversation id to rename/share/delete — `onUnfeature` is its only action.
  onShare?: () => void;
  onRename?: () => void;
  // Retitles the conversation from its transcript, for a session the model
  // couldn't name at the time or whose topic has moved on since.
  onAutoRename?: () => void;
  isAutoRenaming?: boolean;
  onDelete?: () => void;
  // When set, the menu shows only "Unfeature session" instead of the usual
  // Rename/Share/Delete — used for a row in the "Featured Sessions" view,
  // where unfeaturing is the one action that view exists for.
  onUnfeature?: () => void;
}

export const ConversationActionsMenu = ({
  onShare,
  onRename,
  onAutoRename,
  isAutoRenaming,
  onDelete,
  onUnfeature,
}: ConversationActionsMenuProps) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Conversation actions"
          className="size-6 bg-transparent text-muted-foreground opacity-0 transition-opacity pointer-events-none hover:text-foreground group-hover:pointer-events-auto group-hover:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100 data-[state=open]:pointer-events-auto data-[state=open]:opacity-100"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <MoreVertical className="size-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={cn(onUnfeature ? "w-40" : "w-44")}>
        {onUnfeature ? (
          <DropdownMenuItem onSelect={onUnfeature}>Unfeature session</DropdownMenuItem>
        ) : (
          <>
            {onAutoRename && (
              <DropdownMenuItem onSelect={onAutoRename} disabled={isAutoRenaming}>
                {isAutoRenaming ? <Loader2 className="animate-spin" /> : <Sparkles />}
                Auto-rename
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={onRename}>
              <Pencil />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onShare}>
              <Share2 />
              Share…
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onDelete} variant="destructive">
              <Trash2 />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
